/**
 * EmailVerifyBanner — banner-center source (2026-08-14 owner directive).
 *
 * Any signed-in account with an unconfirmed email sees a reminder to confirm
 * it, saying what confirming does. Dismissal is DAILY: the X hides the banner
 * only until the next 5:00 AM local, so it returns every morning until the
 * address is confirmed (same mechanism as the trial banner).
 *
 * Sign-in requirements (docs/PLAN-signin-requirements-20261029.md, decision 3):
 * when the platform requires a confirmed email by a date, this notice carries
 * that countdown (the two-step countdown's own rule,
 * signInRequirementBannerPolicy.ts): "confirm your email by {date}", calm and
 * dismissible for the day while more than three days remain, not dismissible
 * from then, critical on the day. The date comes from the person's own status
 * (useSignInSecurityStatus, the band's shared read, where the client lever
 * admits the tab's workspace). The old "unverified accounts may be paused" had
 * no date and nothing behind it; the words are signInRequirementCopy.ts.
 *
 * Eligibility re-checks on auth state and after the user returns from their
 * inbox (visibilitychange → user.reload()), so the banner disappears the
 * moment verification lands without a full page refresh.
 *
 * The resend action goes through requestBrandedVerificationEmail (the shared
 * client entry point for verification emails — email backoff plan); the raw
 * sendEmailVerification lives only inside that helper as its fallback.
 */

import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { requestBrandedVerificationEmail } from '../../lib/identity/signInOrLink';
import { useAuth } from '../../context/AuthContext';
import { useBannerSurface } from './bannerSurface';
import { BannerBusContext, useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from './bannerDismissal';
import { signInRequirementsShow } from '../../services/identity/signInRequirementsLever';
import { resolveTabWorkspace } from '../../services/prest0nRuntime/tabWorkspace';
import { appPathname } from '../../design/previewPath';
import { isSettingsPath } from './provePhoneBannerPolicy';
import { useSignInSecurityStatus } from '../../hooks/useSignInSecurityStatus';
import { SIGN_IN_REQUIREMENT_COPY } from './signInRequirementCopy';
import { countdownDismissible, countdownStage, countdownTone, pressingRequirement } from './signInRequirementBannerPolicy';

const WORDS = SIGN_IN_REQUIREMENT_COPY.email;

const DISMISS_KEY = 'prest0n_emailverify_dismissed';

const ResendButton = styled.button`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #8a6b2a;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.7; cursor: default; }
`;

export const EmailVerifyBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onAppSurface = useBannerSurface();
  const { pathname } = useLocation();
  const onSettings = isSettingsPath(appPathname(pathname));
  const { user: pointer } = useAuth();

  const [user, setUser] = useState<User | null>(null);
  const [verified, setVerified] = useState<boolean>(true);
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDailyDismissal(DISMISS_KEY));
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setVerified(u ? u.emailVerified : true);
    });
    return unsub;
  }, []);

  // Re-check when the tab regains focus — the user likely just clicked the
  // verification link in their inbox.
  useEffect(() => {
    const onVisible = () => {
      const u = getAuth().currentUser;
      if (!u || u.emailVerified) return;
      u.reload()
        .then(() => setVerified(getAuth().currentUser?.emailVerified ?? true))
        .catch(() => { /* transient — keep current state */ });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // The platform's dated email requirement, from the person's own status where the client lever admits the tab's
  // workspace: its day, and how loud the notice is now.
  const uid = user && !user.isAnonymous ? user.uid : null;
  const accountId = uid ? resolveTabWorkspace({ uid, accountFromPointer: pointer?.accountId }).accountId : null;
  const status = useSignInSecurityStatus(uid, !verified && !!accountId && signInRequirementsShow(accountId), onSettings);
  const requirement = pressingRequirement(status, 'email-confirmed');
  const dated = requirement ? { stage: countdownStage(requirement, Date.now()), dueAtMs: requirement.dueAtMs } : null;
  const dismissible = dated ? countdownDismissible(dated.stage) : true;

  const eligible =
    !!user &&
    !!user.email &&
    !verified &&
    onAppSurface &&
    !(dismissible && isDismissalActive(dismissedAt));

  const handleResend = () => {
    const u = getAuth().currentUser;
    if (!u || resendState !== 'idle') return;
    setResendState('sending');
    // Never rejects (branded callable with the raw client send as its
    // internal fallback) — the catch is belt-and-braces for the state machine.
    requestBrandedVerificationEmail(u)
      .then(() => setResendState('sent'))
      .catch(() => setResendState('idle'));
  };

  const handleDismiss = () => {
    writeDailyDismissal(DISMISS_KEY);
    setDismissedAt(readDailyDismissal(DISMISS_KEY));
  };

  // The words, one place for both designs' bands (./bannerNotice.ts, ./signInRequirementCopy.ts). The phone line
  // (2026-09-13): undated, the address is what makes this actionable — you check THAT inbox — so it survives; dated,
  // the day does. The rest is one tap away.
  const email = user?.email ?? '';
  const message = WORDS.words(email, dated);
  const shortMessage = WORDS.wordsShort(email, dated);
  const resendLabel = resendState === 'sent' ? WORDS.sent : resendState === 'sending' ? WORDS.sending : WORDS.resend;
  const resendShortLabel = resendState === 'sent' ? WORDS.sent : resendState === 'sending' ? WORDS.sending : WORDS.resendShort;
  const notice: BannerNotice = {
    words: message,
    shortWords: shortMessage,
    action: {
      label: resendLabel,
      shortLabel: resendShortLabel,
      onPress: handleResend,
      disabled: resendState !== 'idle',
    },
  };

  const descriptor: BannerDescriptor | null = eligible
    ? {
        id: 'email-verify',
        tone: dated ? countdownTone(dated.stage, 'warn') : 'warn',
        dismissible,
        onDismiss: dismissible ? handleDismiss : undefined,
        notice,
        shortContent: (
          <>
            <span>{shortMessage}</span>
            <ResendButton type="button" onClick={handleResend} disabled={resendState !== 'idle'}>
              {resendShortLabel}
            </ResendButton>
          </>
        ),
        content: (
          <>
            <span>{message}</span>
            <ResendButton type="button" onClick={handleResend} disabled={resendState !== 'idle'}>
              {resendLabel}
            </ResendButton>
          </>
        ),
      }
    : null;

  useRegisterBanner(bus ? descriptor : null);

  // This source is banner-center-native (no legacy standalone strip existed);
  // outside the center it renders nothing.
  return null;
};

export default EmailVerifyBanner;
